import { z } from 'zod';
import { eventSchema } from './envelope';

export const COMMUNICATION_POST_PUBLISHED_V1 = 'communication.post.published.v1' as const;
export const COMMUNICATION_POST_UPDATED_V1 = 'communication.post.updated.v1' as const;
export const COMMUNICATION_POST_WITHDRAWN_V1 = 'communication.post.withdrawn.v1' as const;
export const COMMUNICATION_POST_DELETED_V1 = 'communication.post.deleted.v1' as const;
export const COMMUNICATION_POST_DUPLICATED_V1 = 'communication.post.duplicated.v1' as const;
export const COMMUNICATION_POST_SUBMITTED_V1 = 'communication.post.submitted.v1' as const;
export const COMMUNICATION_POST_APPROVED_V1 = 'communication.post.approved.v1' as const;
export const COMMUNICATION_POST_REJECTED_V1 = 'communication.post.rejected.v1' as const;
export const COMMUNICATION_WEBSITE_FEED_CHANGED_V1 =
  'communication.website_feed.changed.v1' as const;

/**
 * Een bericht is gepubliceerd, met de ontvangers erin.
 *
 * De ontvangers gaan mee in het event en niet als "vraag het maar op": `notifications`
 * (Stap 9) moet weten wie er een melding krijgt, en dat moet ook kloppen als het bericht
 * daarna wordt bijgewerkt. Een lijst in het event is een momentopname; een verwijzing zou
 * dat niet zijn.
 *
 * Bij veel ontvangers wordt het event in stukken gepubliceerd van maximaal
 * `RECIPIENT_CHUNK_SIZE`, elk met `chunk` en `chunkCount` erin. Een fabriek met 25.000
 * mensen levert anders één event van enkele megabytes, en dat is precies het soort rij dat
 * een wachtrij laat omvallen.
 */
export const RECIPIENT_CHUNK_SIZE = 10_000;

export const postPublishedV1Schema = eventSchema({
  postId: z.guid(),
  version: z.number().int(),
  title: z.string(),
  requiresAcknowledgement: z.boolean(),
  audienceDescription: z.string(),
  sourceLanguage: z.string(),
  recipientIds: z.array(z.guid()),
  /** 1-gebaseerd; `chunkCount` is het totaal. Eén stuk betekent `1` van `1`. */
  chunk: z.number().int().min(1),
  chunkCount: z.number().int().min(1),
  recipientCount: z.number().int(),
  /** "Namens HR" (Stap 15): het afzenderlabel staat ook in de pushmelding. */
  senderLabel: z.string().nullable().optional(),
  /** Verplicht (Stap 16): `notifications` negeert dan de voorkeuren van de ontvanger. */
  mandatory: z.boolean().optional(),
  /** De soort mededeling; `veiligheid` valt in de meldingscategorie Veiligheid (Stap 16). */
  category: z.string().optional(),
});

export const postUpdatedV1Schema = eventSchema({
  postId: z.guid(),
  version: z.number().int(),
  title: z.string(),
});

export const postWithdrawnV1Schema = eventSchema({
  postId: z.guid(),
  reason: z.string().nullable(),
});

/**
 * Een concept is definitief verwijderd (Extra 3).
 *
 * **Met de titel, zonder de inhoud.** Stap 18 moet in het auditspoor kunnen zetten *wat* er weg
 * is - "Veiligheidsprocedure heftruckzone" zegt dat, en de volledige tekst zou betekenen dat een
 * verwijderd bericht gewoon in de eventtabel blijft staan. Dan is "verwijderd" een woord.
 *
 * Wie het deed, staat in de envelop (`actor`); dat geldt voor elk event en hoeft hier niet
 * herhaald te worden.
 */
export const postDeletedV1Schema = eventSchema({
  postId: z.guid(),
  title: z.string(),
});

/**
 * Een bericht is gedupliceerd (Extra 3).
 *
 * `replacesPostId` is gezet bij een **correctie** en leeg bij een gewone kopie. Het verschil doet
 * ertoe voor het auditspoor: het eerste is "dit bericht verving dat bericht", het tweede is
 * "iemand begon een nieuw bericht met dezelfde tekst".
 */
export const postDuplicatedV1Schema = eventSchema({
  postId: z.guid(),
  sourcePostId: z.guid(),
  replacesPostId: z.guid().nullable(),
  title: z.string(),
});

export type PostPublishedV1 = z.infer<typeof postPublishedV1Schema>;
export type PostDeletedV1 = z.infer<typeof postDeletedV1Schema>;
export type PostDuplicatedV1 = z.infer<typeof postDuplicatedV1Schema>;
export type PostUpdatedV1 = z.infer<typeof postUpdatedV1Schema>;
export type PostWithdrawnV1 = z.infer<typeof postWithdrawnV1Schema>;

/**
 * Ingediend ter goedkeuring (Stap 15), met wie het mag goedkeuren: `notifications` kan de
 * goedkeurders niet zelf uitrekenen (ADR-0021).
 */
export const postSubmittedV1Schema = eventSchema({
  postId: z.guid(),
  title: z.string(),
  submittedBy: z.guid(),
  approverIds: z.array(z.guid()),
});
export type PostSubmittedV1 = z.infer<typeof postSubmittedV1Schema>;

export const postApprovedV1Schema = eventSchema({
  postId: z.guid(),
  title: z.string(),
  submittedBy: z.guid().nullable(),
});
export type PostApprovedV1 = z.infer<typeof postApprovedV1Schema>;

/** Met de reden: de indiener leest ze in zijn melding en bij het bericht. */
export const postRejectedV1Schema = eventSchema({
  postId: z.guid(),
  title: z.string(),
  submittedBy: z.guid().nullable(),
  reason: z.string(),
});
export type PostRejectedV1 = z.infer<typeof postRejectedV1Schema>;

/**
 * De websitefeed van de organisatie ging open of dicht (WEB-1, ADR-0097).
 *
 * Een beheerdershandeling met gevolgen buiten de app: vanaf dat moment kan iedereen op internet de
 * gemarkeerde berichten lezen. Daarom staat ze in het auditspoor, met wie het deed.
 */
export const websiteFeedChangedV1Schema = eventSchema({
  enabled: z.boolean(),
  changedBy: z.guid().nullable(),
});
export type WebsiteFeedChangedV1 = z.infer<typeof websiteFeedChangedV1Schema>;

export const communicationEventCatalog = {
  [COMMUNICATION_WEBSITE_FEED_CHANGED_V1]: websiteFeedChangedV1Schema,
  [COMMUNICATION_POST_SUBMITTED_V1]: postSubmittedV1Schema,
  [COMMUNICATION_POST_APPROVED_V1]: postApprovedV1Schema,
  [COMMUNICATION_POST_REJECTED_V1]: postRejectedV1Schema,
  [COMMUNICATION_POST_PUBLISHED_V1]: postPublishedV1Schema,
  [COMMUNICATION_POST_UPDATED_V1]: postUpdatedV1Schema,
  [COMMUNICATION_POST_WITHDRAWN_V1]: postWithdrawnV1Schema,
  [COMMUNICATION_POST_DELETED_V1]: postDeletedV1Schema,
  [COMMUNICATION_POST_DUPLICATED_V1]: postDuplicatedV1Schema,
} as const;
